import { Button, Card, FormFileUpload, FormattedMessageDiv, WizardForm, useWizardContext, useWizardFormContext } from "@odmonorepo/core-components";
import { uploadDocuments } from "merchant-api/spiceroad";
import { useEffect } from "react";
import { Accordion, Stack } from "react-bootstrap";
import * as yup from 'yup';

interface StatementUploaderProps {
  variation?: string;
  defaultAccordionOpen?: boolean;
}

const fileTest = yup
  .mixed<FileList>()
  .test('required', 'Upload a bank statement to continue.', file => file && file?.length > 0)
  .test('size', 'File must be smaller than 5MB.', file => file && file[0]?.size < 5 * 1024 * 1024)
  .test('type', 'File must a PDF.', file => file && file[0]?.type === 'application/pdf')

const schema = yup.object().shape({
  file1: fileTest,
  file2: fileTest,
  file3: fileTest,
});

const previousMonth = (prev = 1): string => {
  const currentDate = new Date();
  currentDate.setMonth(currentDate.getMonth() - prev);
  const month = currentDate.toLocaleString('default', { month: 'long' });
  const year = currentDate.getFullYear();
  return `${month} ${year} Statement`;
};

export const StatementUploader = ({ variation, defaultAccordionOpen }: StatementUploaderProps) => {
  const {upload, documentsSending, response} = uploadDocuments();
  const { nextStep } = useWizardContext();

  const handleSubmit = async (values: typeof schema.__outputType) => {
    const formData = new FormData();
    Object.values(values).forEach(fileList => formData.append('files', fileList.item(0)!));
    upload(formData);
  }

  useEffect(() => {
    response && nextStep();
  }, [response]);

  const statementUploaderControl = (
    <Stack gap={3}>
      <FormattedMessageDiv id='statementUploader.title' className="h4 text-center"/>
      <FormattedMessageDiv id='statementUploader.subtitle'
      values={{ help: (txt: any) => <a href="https://www.ondeck.com/how-to-download-your-bank-statements">{txt}</a> }}
      />
      <WizardForm schema={schema} reValidateMode='onChange' onSubmit={v =>handleSubmit(v as typeof schema.__outputType)}>
        <Stack className="text-start" gap={1}>
          <FormFileUpload data-testid="file1" name="file1" label={previousMonth(1)} accept="application/pdf" useYupErrors/>
          <FormFileUpload data-testid="file2" name="file2" label={previousMonth(2)} accept="application/pdf" useYupErrors/>
          <FormFileUpload data-testid="file3" name="file3" label={previousMonth(3)} accept="application/pdf" useYupErrors/>
          <SubmitButton loading={documentsSending} text="Submit" />
        </Stack>
      </WizardForm>
    </Stack>
  );

  const statementUploaderBoth = (
    <Accordion className="statement-uploader-accordion" defaultActiveKey={defaultAccordionOpen ? '0' : undefined}>
      <Accordion.Item eventKey="0" className="border-0">
        <Accordion.Header>
          <FormattedMessageDiv id='statementUploader.title.experiment' className="h4 text-start text-primary mb-0"/>
        </Accordion.Header>
        <Accordion.Body>
          <FormattedMessageDiv id='statementUploader.subtitle.experiment'
          className="text-start mb-6"
          values={{ help: (txt: any) => <a href="https://www.ondeck.com/how-to-download-your-bank-statements">{txt}</a> }}
          />
          <WizardForm schema={schema} reValidateMode='onChange' onSubmit={v =>handleSubmit(v as typeof schema.__outputType)}>
            <Stack className="text-start" gap={1}>
              <FormFileUpload data-testid="file1" name="file1" label={previousMonth(1)} accept="application/pdf" useYupErrors/>
              <FormFileUpload data-testid="file2" name="file2" label={previousMonth(2)} accept="application/pdf" useYupErrors/>
              <FormFileUpload data-testid="file3" name="file3" label={previousMonth(3)} accept="application/pdf" useYupErrors/>
              <SubmitButton loading={documentsSending} text="Submit Statements" />
            </Stack>
          </WizardForm>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );

  const showElement = () => {
    if (variation === 'pdf_accordion_and_language') {
      return statementUploaderBoth;
    } else {
      return statementUploaderControl;
    }
  }

  return (
    <Card>
      {showElement()}
    </Card>
  );
};

type SubmitButtonProps = {
  loading?: boolean;
  text?: React.ReactNode;
};

const SubmitButton = ({ loading = false, text = '' }: SubmitButtonProps) => {
  const { formName } = useWizardFormContext();
  return <div className="mt-3">
    <Button type="submit" className="px-8 d-block mx-auto w-100" loading={loading} form={formName} style={{ width: '100%', maxWidth: '303px' }}>
      {text}
    </Button>
  </div>
}

export default StatementUploader;
